import {useState} from "react";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import ImagePicker from "./imagePicker.view";
import {RemoveImageFromPackCommand} from "../../commands/removeImageFromPack.command";
import {offerOrphanImageCleanup} from "../../utils/orphanImageCleanup";

interface SlotRef {
    level: number;
    variation: number;
    currentKey?: string;
}

interface AssetPackGridProps {
    packId: string;
    // Currently-previewed image key — its slot is highlighted.
    selectedKey?: string;
    // Flyout mode: click a filled slot to make it the previewed/edited image.
    onSelectSlot?(key: string): void;
    // Upload mode (asset-pack popup): when provided, OVERRIDES slot clicks —
    // both filled and empty slots call this with the slot coordinates (key set
    // for a filled slot, undefined for a '+' placeholder) so the host can run a
    // file-open dialog + upload. The grid's own picker is bypassed.
    onUploadSlot?(slot: {level: number; variation: number; key?: string}): void;
    // Whether the hover ✕ (remove from pack) is offered. Default true.
    allowRemove?: boolean;
    // Drag a filled slot into the Workshop (carries the image key). The host
    // wires these to its dragging-key state so the Workshop's snap-ghost shows.
    onDragImageStart?(key: string): void;
    onDragImageEnd?(): void;
}

// The Level × Variation grid for an entity's asset pack, embedded in the Image
// Preview flyout (replaces the old standalone popup). Filled slots select for
// preview; empty placeholder slots open the picker to assign/upload; the ✕
// removes the association (archive-prompt only if it orphans the image).
function AssetPackGrid(props: AssetPackGridProps) {
    const {state, executeCommand, goBack} = useIntegrator();
    const {getLocalizationValue, getImageUrl, setNotification, choose, attributeManifest} = useApp();
    const [pickerSlot, setPickerSlot] = useState<SlotRef | undefined>(undefined);
    const [hoveredSlot, setHoveredSlot] = useState<{level: number; variation: number} | undefined>(undefined);

    const id = props.packId;
    const images = state.imagePacks[id];

    const isBuildingPack = state.buildings.some(b => b.id === id);
    const isUnitPack = state.units.some(u => u.id === id);
    // Buildings + units can level up; tiles/resources only render pack[0], so a
    // "next level" placeholder would be a trap for them.
    const isLevelablePack = isBuildingPack || isUnitPack;
    // First visible column = the building's startLevel (rows below are hidden
    // dead slots), clamped so a short pack still shows its one real slot. Levels
    // are shown 1-based.
    const startLevel = Math.max(0, state.buildings.find(b => b.id === id)?.startLevel ?? 0);
    const firstVisibleColumn = isBuildingPack
        ? Math.max(0, Math.min(startLevel, (images?.length ?? 1) - 1))
        : 0;

    async function handleRemove(level: number, variation: number, key: string) {
        // Removes the pack's reference to this image (the association), not the
        // image itself. Refuse to leave an empty middle level (runtime can't
        // handle it); otherwise drop the slot and, if that orphaned the image,
        // offer to archive/delete it.
        const pack = state.imagePacks[id];
        if (pack && pack[level]?.length === 1 && level !== pack.length - 1) {
            const shown = level - firstVisibleColumn + 1;
            setNotification({
                title: "Can't remove this slot",
                message: `Removing the only image at Level ${shown} would leave an empty level in the middle of the progression, which the runtime can't handle. Add another image at Level ${shown} first, or remove higher levels first.`,
            });
            return;
        }
        executeCommand(new RemoveImageFromPackCommand(id, level, variation));
        await offerOrphanImageCleanup(key, {state, getLocalizationValue, attributeManifest, choose, executeCommand, goBack});
    }

    function constructNextKey(key: string, level: number, variation: number): string {
        return `${key}_${level}_${variation}`;
    }

    function constructPack(): string[][] {
        const pack: string[][] = [];
        for (let i = 0; i < images.length; i++) {
            pack.push([]);
            const variations = images[i];
            for (let j = 0; j < variations.length; j++) pack[i].push(variations[j]);
            pack[i].push(constructNextKey(id, i, variations.length));
        }
        if (isLevelablePack) pack.push([constructNextKey(id, images.length, 0)]);
        return pack;
    }

    if (!images) return null;

    const SLOT = 40;
    return (
        <div className={'layout horizontal gap'}
             style={{gap: 6, overflowX: 'auto', alignItems: 'flex-start',
                 // Small inset so the selected slot's box-shadow ring isn't clipped
                 // by the scroll container's edge (esp. the flush leftmost slot).
                 padding: 3}}>
            {constructPack().map((levels, i) => {
                if (i < firstVisibleColumn) return null;
                return (
                    <div key={i} className={'layout vertical gap'} style={{gap: 4}}>
                        {isLevelablePack &&
                            <p style={{fontSize: 11, color: 'rgba(255,255,255,0.66)', whiteSpace: 'nowrap'}}>
                                Level {i - firstVisibleColumn + 1}
                            </p>}
                        {levels.map((variation, j) => {
                            const isExisting = j < (images[i]?.length ?? 0);
                            const isHovered = hoveredSlot?.level === i && hoveredSlot?.variation === j;
                            const isSelected = isExisting && variation === props.selectedKey;
                            return (
                                <div key={j}
                                     onClick={() => {
                                         if (props.onUploadSlot) props.onUploadSlot({level: i, variation: j, key: isExisting ? variation : undefined});
                                         else if (isExisting) props.onSelectSlot?.(variation);
                                         else setPickerSlot({level: i, variation: j, currentKey: undefined});
                                     }}
                                     onMouseEnter={() => isExisting && setHoveredSlot({level: i, variation: j})}
                                     onMouseLeave={() => setHoveredSlot(undefined)}
                                     style={{
                                         cursor: 'pointer', position: 'relative',
                                         width: SLOT, height: SLOT, borderRadius: 4,
                                         backgroundColor: 'black',
                                         // Constant 1px border (just recoloured when selected) keeps every
                                         // slot the same size; the extra "thickness" on the selected slot is
                                         // a box-shadow ring, which doesn't affect layout — so neighbours
                                         // don't shift.
                                         border: `solid 1px ${isSelected ? 'var(--tint-color)' : '#515151'}`,
                                         boxShadow: isSelected ? '0 0 0 1px var(--tint-color)' : undefined,
                                     }}>
                                    {isExisting
                                        ? <img alt={variation} src={getImageUrl(variation)}
                                               draggable={!!props.onDragImageStart}
                                               onDragStart={e => {
                                                   // Carry the image KEY (not the default <img> URL,
                                                   // which the Workshop can't resolve → "not in this branch").
                                                   e.dataTransfer.setData('text/plain', variation);
                                                   e.dataTransfer.effectAllowed = 'copy';
                                                   props.onDragImageStart?.(variation);
                                               }}
                                               onDragEnd={() => props.onDragImageEnd?.()}
                                               style={{width: '100%', height: '100%', borderRadius: 4}}/>
                                        : <div className={'layout vertical center'}
                                               style={{width: '100%', height: '100%', color: 'rgba(255,255,255,0.4)'}}>
                                            <span className={'fa-solid fa-plus'} style={{fontSize: 10}}/>
                                          </div>}
                                    {isExisting && isHovered && props.allowRemove !== false && (
                                        <button onClick={e => { e.stopPropagation(); handleRemove(i, j, variation); }}
                                                title={'Remove this image from the pack'}
                                                style={{
                                                    position: 'absolute', top: -6, right: -6,
                                                    width: 16, height: 16, padding: 0, borderRadius: '50%',
                                                    border: 'solid 1px #515151', background: 'var(--background-color)',
                                                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                            <span className={'fa-solid fa-xmark'} style={{fontSize: 9}}/>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
            {pickerSlot && (
                <ImagePicker target={{kind: 'packSlot', packId: id, level: pickerSlot.level, variation: pickerSlot.variation}}
                             currentKey={pickerSlot.currentKey}
                             onClose={() => setPickerSlot(undefined)}/>
            )}
        </div>
    );
}

export default AssetPackGrid;
